import clsx from 'clsx';
import React from 'react';
import { HUE_TINT, type Hue, hueFor } from './hue';
import { IconInfo, IconSparkle, IconUser, IconWarning } from './icons';

/**
 * Initials avatar. Neutral by default; accent marks "Arthur" (the AI).
 * `hue="auto"` tints a named person from `identity` (or `name`), so the same
 * guest is the same colour on every page. With no name, a plain profile icon.
 */
export const Avatar: React.FC<{
  name?: string | null;
  /** Hash source for `hue="auto"` when the name is missing or unstable (phone, id). */
  identity?: string | number | null;
  /** Glyph when there is no name. Omit it for a profile icon. */
  fallback?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  arthur?: boolean;
  hue?: Hue | 'auto' | null;
  className?: string;
}> = ({
  name,
  identity,
  fallback,
  size = 'sm',
  arthur,
  hue = null,
  className,
}) => {
  const initials = arthur
    ? ''
    : (name || '')
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase())
        .join('');
  const glyph = initials || fallback;
  const tint =
    arthur || !glyph || !hue
      ? null
      : hue === 'auto'
        ? hueFor(identity ?? name)
        : hue;
  const iconSize =
    size === 'xs' ? 10 : size === 'sm' ? 12 : size === 'lg' ? 16 : 14;
  return (
    <span
      aria-hidden
      className={clsx(
        'inline-flex shrink-0 select-none items-center justify-center rounded-full font-semibold',
        size === 'xs' && 'h-5 w-5 text-[9px]',
        size === 'sm' && 'h-6 w-6 text-v2-11',
        size === 'md' && 'h-8 w-8 text-v2-12',
        size === 'lg' && 'h-10 w-10 text-v2-14',
        arthur
          ? 'bg-v2-accent text-v2-on-accent'
          : tint
            ? HUE_TINT[tint]
            : 'bg-v2-sunken text-v2-fg-2 ring-1 ring-inset ring-v2-line',
        className,
      )}
    >
      {arthur ? (
        <IconSparkle size={iconSize} />
      ) : glyph ? (
        glyph
      ) : (
        <IconUser size={iconSize} />
      )}
    </span>
  );
};

const GLYPH_SIZE = {
  xs: 'h-5 w-5 rounded-v2-sm',
  sm: 'h-7 w-7 rounded-v2',
  md: 'h-8 w-8 rounded-[10px]',
  lg: 'h-11 w-11 rounded-v2-lg',
};

/** Small rounded icon tile in a categorical hue. */
export const HueGlyph: React.FC<{
  hue: Hue;
  size?: keyof typeof GLYPH_SIZE;
  className?: string;
  children: React.ReactNode;
}> = ({ hue, size = 'md', className, children }) => (
  <span
    aria-hidden
    className={clsx(
      'inline-flex shrink-0 items-center justify-center',
      GLYPH_SIZE[size],
      HUE_TINT[hue],
      className,
    )}
  >
    {children}
  </span>
);

export const Skeleton: React.FC<{
  className?: string;
  width?: number | string;
}> = ({ className, width }) => (
  <span
    aria-hidden
    className={clsx('v2-skeleton inline-block h-3', className)}
    style={{ width }}
  />
);

export const EmptyState: React.FC<{
  title: string;
  description?: string;
  action?: React.ReactNode;
  icon?: React.ReactNode;
  compact?: boolean;
}> = ({ title, description, action, icon, compact }) => (
  <div
    className={clsx(
      'flex flex-col items-center justify-center text-center',
      compact ? 'px-4 py-8' : 'px-6 py-16',
    )}
  >
    {icon && (
      <div className="mb-3 flex h-8 w-8 items-center justify-center rounded-v2 border border-v2-line text-v2-fg-3">
        {icon}
      </div>
    )}
    <p className="text-v2-13 font-medium text-v2-fg">{title}</p>
    {description && (
      <p className="mt-1 max-w-[36ch] text-v2-12 text-v2-fg-3">{description}</p>
    )}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

/**
 * Callout: inline, bordered message for missing information, errors and
 * notices. Never stacked more than once.
 */
export const Callout: React.FC<{
  tone?: 'neutral' | 'warning' | 'danger' | 'accent';
  title?: string;
  children?: React.ReactNode;
  className?: string;
  icon?: React.ReactNode;
  /**
   * A tinted well with no border, for a note that sits inside a card or an
   * open step, where a bordered box would read as a card in a card.
   */
  well?: boolean;
}> = ({ tone = 'neutral', title, children, className, icon, well }) => {
  const Icon =
    icon ??
    (tone === 'warning' || tone === 'danger' ? (
      <IconWarning size={14} />
    ) : (
      <IconInfo size={14} />
    ));
  return (
    <div
      role={tone === 'danger' ? 'alert' : undefined}
      className={clsx(
        'flex gap-2.5 px-3 py-2.5 text-v2-12 leading-[18px]',
        well ? 'rounded-v2-lg border-0 px-3.5 py-3' : 'rounded-v2 border',
        tone === 'neutral' && 'border-v2-line bg-v2-sunken/60 text-v2-fg-2',
        tone === 'accent' && 'border-v2-accent/25 bg-v2-accent/5 text-v2-fg-2',
        tone === 'warning' &&
          'border-v2-warning/30 bg-v2-warning/[0.07] text-v2-fg-2',
        tone === 'danger' &&
          'border-v2-danger/30 bg-v2-danger/[0.06] text-v2-fg-2',
        className,
      )}
    >
      <span
        className={clsx(
          'mt-px shrink-0',
          tone === 'warning' && 'text-v2-warning',
          tone === 'danger' && 'text-v2-danger',
          tone === 'accent' && 'text-v2-accent',
          tone === 'neutral' && 'text-v2-fg-3',
        )}
      >
        {Icon}
      </span>
      <div className="min-w-0 flex-1">
        {title && <p className="font-medium text-v2-fg">{title}</p>}
        {children}
      </div>
    </div>
  );
};

/**
 * Marks UI that demonstrates a capability the backend does not persist yet.
 * Keeps the prototype honest without cluttering the layout.
 */
export const Proposed: React.FC<{ className?: string; title?: string }> = ({
  className,
  title = 'Proposed capability — not persisted by the current backend',
}) => (
  <span
    title={title}
    aria-label={title}
    className={clsx(
      'inline-flex h-4 items-center rounded-v2-sm border border-dashed border-v2-fg-4/70 px-1 text-[10px] font-medium uppercase tracking-[0.04em] text-v2-fg-3',
      className,
    )}
  >
    Proposed
  </span>
);

export const Kbd: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <kbd className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-v2-sm border border-v2-line-strong/80 bg-v2-surface px-1 font-sans text-v2-11 text-v2-fg-3">
    {children}
  </kbd>
);

/**
 * Inline figure for toolbars ("128 calls · 4:12 avg."). Replaces metric
 * cards: the number sits next to the thing it describes.
 * `tone="data"` colours the value (only) in the data hue — use it when the
 * figure summarises a measurement that the table below also visualises.
 */
export const Figure: React.FC<{
  label: string;
  value: React.ReactNode;
  tone?: 'default' | 'data';
  className?: string;
}> = ({ label, value, tone = 'default', className }) => (
  <div className={clsx('flex items-baseline gap-1.5', className)}>
    <span
      className={clsx(
        'v2-num text-v2-14 font-semibold',
        tone === 'data' ? 'text-v2-data' : 'text-v2-fg',
      )}
    >
      {value}
    </span>
    <span className="text-v2-12 text-v2-fg-3">{label}</span>
  </div>
);

/**
 * Compact horizontal bar for a single measurement inside a dense row
 * (call duration, spend against budget). Data hue on a muted data track;
 * `aria-hidden` because the number it accompanies is the accessible value.
 * `value` is 0–1; values below `min` are clamped so short bars stay visible.
 */
export const Meter: React.FC<{
  value: number;
  /** Lower clamp as a fraction of the track (default 0.1). */
  min?: number;
  className?: string;
  /** Fill utility. Defaults to the data hue. */
  fillClassName?: string;
  /** Spread onto the filled bar (e.g. a `data-testid`). */
  barProps?: React.HTMLAttributes<HTMLSpanElement> &
    Record<`data-${string}`, string>;
}> = ({ value, min = 0.1, className, fillClassName, barProps }) => {
  const clamped = Number.isFinite(value)
    ? Math.min(Math.max(value, min), 1)
    : min;
  // One decimal keeps 0.1 → "10%" rather than "10.000000000000002%".
  const pct = Math.round(clamped * 1000) / 10;
  return (
    <span
      aria-hidden
      className={clsx(
        'inline-block h-1 w-20 overflow-hidden rounded-full bg-v2-data-track',
        className,
      )}
    >
      <span
        {...barProps}
        className={clsx(
          'block h-full rounded-full',
          fillClassName ?? 'bg-v2-data',
          barProps?.className,
        )}
        style={{ width: `${pct}%`, ...barProps?.style }}
      />
    </span>
  );
};

/**
 * One-line reason next to a control that is hidden or unavailable
 * (capability missing, precondition unmet). Pairs with Button `disabledReason`.
 */
export const Reason: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => (
  <p className={clsx('text-v2-12 leading-[18px] text-v2-fg-3', className)}>
    {children}
  </p>
);
